import React, { useMemo, useState, useCallback } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Button,
  useTheme,
  Paper,
} from '@mui/material'
import { useAllRepairTrucks, useLogistics, useRepairTrucksForTrucks } from '@/hooks/logistics/useLogistics'
import { usePickupLocations } from '@/hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'
import { useClientUsers, useUsers } from '@/hooks/useUsers'
import { RepairV2TrucksGrid, RepairTrackingView } from '../views'
import type { RepairTruckWithTruck } from '../views/RepairTrackingView'
import { getRepairTruckWorkStatus, getTruckStatus } from '@otw/models/logistics'
import { isActiveRepairTruckStatus, isTruckActiveAtRepairShop } from '@otw/models/repair'
import type { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import { useAuth } from '@/contexts/AuthContext'
import SearchHeader from '../common/SearchHeader'
import { RepairTimelineDialog, TransportTruckCardDialog } from '../dialogs'
import { EditVehicleForm } from '@/components/inventory/forms'
import type { RepairTruck, RepairTruckWorkStatus } from '@otw/models/logistics'
import { vehicleStatusOptions } from '@/constants/inventory'

const ALL_SHOPS = 'ALL' as const
const UNKNOWN_SHOP_ID = '__unknown__' as const
const UNKNOWN_SHOP_LABEL = 'Unknown shop'

const ALL_CUSTOMERS = 'ALL' as const
const UNASSIGNED_CUSTOMER_ID = '__unassigned__' as const
const UNASSIGNED_CUSTOMER_LABEL = 'Unassigned'
const PENDING_CUSTOMER_STATUSES = new Set(['Pending Sale', 'Active Deal'])

const ALL_FILTER = 'ALL' as const
const UNKNOWN_STATUS_FILTER = '__unknown_status__' as const

const WORK_STATUS_FILTER_OPTIONS: Array<{ value: RepairTruckWorkStatus; label: string }> = [
  { value: 'Awaiting estimate', label: 'Awaiting estimate' },
  { value: 'in progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
]

function getInventoryStatusFilterValue(item: RepairTruckWithTruck): string {
  return item.truckWithVehicle.vehicle?.status?.trim() || UNKNOWN_STATUS_FILTER
}

type RepairSortOption =
  | 'default'
  | 'timeline_desc'
  | 'timeline_asc'
  | 'shop_time_desc'
  | 'shop_time_asc'

const REPAIR_SORT_OPTIONS: Array<{ value: RepairSortOption; label: string }> = [
  { value: 'default', label: 'Default' },
  { value: 'timeline_desc', label: 'Recent activity (newest first)' },
  { value: 'timeline_asc', label: 'Recent activity (oldest first)' },
  { value: 'shop_time_desc', label: 'Time at shop (longest first)' },
  { value: 'shop_time_asc', label: 'Time at shop (shortest first)' },
]

type CustomerDisplay = {
  label: string
}

type InventoryStatusDisplay = {
  label: string
  chipColor: 'default' | 'info' | 'error' | 'warning'
}

function formatAverageDuration(ms?: number): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '—'
  const days = Math.floor(ms / (24 * 60 * 60 * 1000))
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`
  const hours = Math.floor(ms / (60 * 60 * 1000))
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'}`
  const minutes = Math.max(0, Math.floor(ms / (60 * 1000)))
  return minutes < 1 ? '< 1 min' : `${minutes} min`
}

function formatLocationAddress(location?: {
  address?: string
  city?: string
  state?: string
  zip?: string
}): string {
  if (!location) return '—'
  const line1 = location.address?.trim() ?? ''
  const line2 = [location.city, location.state, location.zip]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ')
  if (line1 && line2) return `${line1}, ${line2}`
  return line1 || line2 || '—'
}

function getShopLabel(
  locationId: string | undefined,
  repairShopLocations: Array<{ id?: string; name?: string; contact_name?: string }> | undefined
): string {
  if (!locationId) return UNKNOWN_SHOP_LABEL
  const shop = repairShopLocations?.find((s) => s.id === locationId)
  return shop?.name || shop?.contact_name || UNKNOWN_SHOP_LABEL
}

/** Client user id from the most recently created shipment plan for this truck. */
function getLatestShipmentPlanFromTruck(row: TransportTruckWithVehicle) {
  const plans = row.shipmentPlans ?? []
  if (plans.length === 0) return null
  const sorted = [...plans].sort((a, b) => {
    const tA = a.created_at?.toMillis?.() ?? 0
    const tB = b.created_at?.toMillis?.() ?? 0
    return tB - tA
  })
  return sorted[0]
}

/** Client user id from the most recently created shipment plan for this truck. */
function getClientUserIdFromTruck(row: TransportTruckWithVehicle): string | undefined {
  return getLatestShipmentPlanFromTruck(row)?.client_user_id
}

function getCustomerLabel(
  clientUserId: string | undefined,
  clientUsers: Array<{ id: string; displayName?: string; email?: string }> | undefined
): string {
  if (!clientUserId) return UNASSIGNED_CUSTOMER_LABEL
  const user = clientUsers?.find((u) => u.id === clientUserId)
  return user?.displayName ?? user?.email ?? UNASSIGNED_CUSTOMER_LABEL
}

/** Returns true if the truck/vehicle matches the search term (VIN, make, model, year, stock number, client name). */
function truckMatchesSearch(
  row: TransportTruckWithVehicle,
  getCustomerLabelForRow: (row: TransportTruckWithVehicle) => string,
  term: string
): boolean {
  const t = term.trim().toLowerCase()
  if (!t) return true
  const vin = row.truck?.vin ?? ''
  const vehicle = row.vehicle
  const make = vehicle?.make ?? ''
  const model = vehicle?.model ?? ''
  const year = vehicle?.year != null ? String(vehicle.year) : ''
  const stockNumber = vehicle?.stock_number ?? ''
  const clientName = getCustomerLabelForRow(row)
  const haystacks = [vin, make, model, year, stockNumber, clientName].map((s) =>
    String(s).toLowerCase()
  )
  return haystacks.some((h) => h.includes(t))
}

function timestampToMs(value: unknown): number {
  if (!value) return 0
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 0 : value.getTime()
  if (typeof value === 'object') {
    const maybe = value as {
      toMillis?: () => number
      toDate?: () => Date
      seconds?: number
      _seconds?: number
    }
    if (typeof maybe.toMillis === 'function') {
      const ms = maybe.toMillis()
      return Number.isFinite(ms) ? ms : 0
    }
    if (typeof maybe.toDate === 'function') {
      const date = maybe.toDate()
      return Number.isNaN(date.getTime()) ? 0 : date.getTime()
    }
    const seconds = maybe.seconds ?? maybe._seconds
    if (typeof seconds === 'number' && Number.isFinite(seconds)) return seconds * 1000
  }
  return 0
}

/** Most recent repair timeline activity (work notes + work status history). */
function getLatestTimelineActivityMs(repairTruck: RepairTruck): number {
  let latest = 0
  for (const note of repairTruck.work_notes ?? []) {
    latest = Math.max(latest, timestampToMs(note.timestamp))
  }
  for (const entry of repairTruck.work_status_history ?? []) {
    latest = Math.max(latest, timestampToMs(entry.timestamp))
  }
  return latest || timestampToMs(repairTruck.created_at)
}

/** Duration of the current/completed repair stint at the shop (ms). */
function getTimeAtShopMs(item: RepairTruckWithTruck, nowMs = Date.now()): number {
  const stintStartMs = timestampToMs(item.repairTruck.created_at)
  if (!stintStartMs) return 0

  const isActiveStint = isActiveRepairTruckStatus(getTruckStatus(item.truckWithVehicle.truck))
  if (isActiveStint) return Math.max(0, nowMs - stintStartMs)

  const completeEntry = [...(item.repairTruck.work_status_history ?? [])]
    .reverse()
    .find((entry) => entry.status === 'complete')
  const completedMs = timestampToMs(completeEntry?.timestamp)
  const latestWorkStatusMs = timestampToMs(
    item.repairTruck.work_status_history?.[item.repairTruck.work_status_history.length - 1]?.timestamp
  )
  const latestWorkNoteMs = timestampToMs(
    item.repairTruck.work_notes?.[item.repairTruck.work_notes.length - 1]?.timestamp
  )
  const stintEndMs = completedMs || latestWorkStatusMs || latestWorkNoteMs || stintStartMs
  return Math.max(0, stintEndMs - stintStartMs)
}

function sortRepairItems(
  rows: RepairTruckWithTruck[],
  sortBy: RepairSortOption,
  options?: { usePriorityDefault?: boolean }
): RepairTruckWithTruck[] {
  const list = [...rows]
  if (sortBy === 'default') {
    if (options?.usePriorityDefault) {
      return list.sort((a, b) => {
        const rankA = a.repairTruck.priority_rank ?? Number.MAX_SAFE_INTEGER
        const rankB = b.repairTruck.priority_rank ?? Number.MAX_SAFE_INTEGER
        if (rankA !== rankB) return rankA - rankB
        return timestampToMs(a.repairTruck.created_at) - timestampToMs(b.repairTruck.created_at)
      })
    }
    return list.sort(
      (a, b) => timestampToMs(b.repairTruck.created_at) - timestampToMs(a.repairTruck.created_at)
    )
  }

  if (sortBy === 'timeline_desc' || sortBy === 'timeline_asc') {
    const direction = sortBy === 'timeline_desc' ? -1 : 1
    return list.sort((a, b) => {
      const diff =
        (getLatestTimelineActivityMs(a.repairTruck) - getLatestTimelineActivityMs(b.repairTruck)) *
        direction
      if (diff !== 0) return diff
      return timestampToMs(b.repairTruck.created_at) - timestampToMs(a.repairTruck.created_at)
    })
  }

  const direction = sortBy === 'shop_time_desc' ? -1 : 1
  const nowMs = Date.now()
  return list.sort((a, b) => {
    const diff = (getTimeAtShopMs(a, nowMs) - getTimeAtShopMs(b, nowMs)) * direction
    if (diff !== 0) return diff
    return timestampToMs(b.repairTruck.created_at) - timestampToMs(a.repairTruck.created_at)
  })
}

const RepairV2Tab: React.FC = () => {
  const theme = useTheme()
  const { userInfo } = useAuth()
  const {
    transportTrucksWithVehicles,
    isLoadingTransportTrucks,
    addRepairTruckNote,
    updateRepairTruckNote,
    deleteRepairTruckNote,
    updateRepairTruckWorkStatus,
    reviewRepairTruckEstimate,
    updateRepairTruckPaid,
    updateRepairTruckBeingPainted,
    updateRepairTruckPriorities,
    isAddingRepairTruckNote,
    isUpdatingRepairTruckNote,
    isDeletingRepairTruckNote,
    isUpdatingRepairTruckWorkStatus,
    isReviewingRepairTruckEstimate,
    isUpdatingRepairTruckPaid,
    updateRepairTruckFlagged,
    isUpdatingRepairTruckFlagged,
    isUpdatingRepairTruckBeingPainted,
    isUpdatingRepairTruckPriorities,
    repairShopAvgDropoffToCompleteMsById,
  } = useLogistics()
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()
  const { clientUsers } = useClientUsers()
  const { users } = useUsers()
  const [innerTab, setInnerTab] = useState(0)
  const [selectedShopId, setSelectedShopId] = useState<string | typeof ALL_SHOPS>(ALL_SHOPS)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | typeof ALL_CUSTOMERS>(
    ALL_CUSTOMERS
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [showPastRepairs, setShowPastRepairs] = useState(false)
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>(ALL_FILTER)
  const [workStatusFilter, setWorkStatusFilter] = useState<string>(ALL_FILTER)
  const [sortBy, setSortBy] = useState<RepairSortOption>('default')
  const [selectedTimelineRepairTruckId, setSelectedTimelineRepairTruckId] = useState<string | null>(null)
  const [selectedTimelineTruckId, setSelectedTimelineTruckId] = useState<string | null>(null)
  const [inventoryFormVehicleId, setInventoryFormVehicleId] = useState<string | null>(null)
  const [transportCardTruckId, setTransportCardTruckId] = useState<string | null>(null)

  const getUserName = useCallback(
    (userId: string): string => {
      if (userInfo?.id === userId) {
        const currentName = [userInfo.first_name, userInfo.last_name].filter(Boolean).join(' ').trim()
        return currentName || userInfo.email || 'Unknown'
      }
      const u = users.find((x) => x.id === userId)
      if (u) {
        const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
        return name || u.email || 'Unknown'
      }
      return 'Unknown'
    },
    [users, userInfo]
  )

  const truckIds = useMemo(
    () => [...new Set(transportTrucksWithVehicles.map((truckWithVehicle) => truckWithVehicle.truck.id))],
    [transportTrucksWithVehicles]
  )
  const repairTrucksMap = useRepairTrucksForTrucks(truckIds)
  const trucksInRepair = useMemo(
    () =>
      transportTrucksWithVehicles.filter((truckWithVehicle) => {
        const status = getTruckStatus(truckWithVehicle.truck)
        if (!isActiveRepairTruckStatus(status)) return false
        const currentLocationId = truckWithVehicle.truck.currentLocationId
        const currentRepairTruck = repairTrucksMap.get(
          `${truckWithVehicle.truck.id}_${currentLocationId ?? ''}`
        )
        return isTruckActiveAtRepairShop({
          truckStatus: status,
          currentLocationId,
          repairShopLocationId: currentRepairTruck?.repair_shop_location_id,
        })
      }),
    [transportTrucksWithVehicles, repairTrucksMap]
  )
  const allRepairTrucks = useAllRepairTrucks()
  const getRepairTruck = useCallback(
    (row: TransportTruckWithVehicle) =>
      repairTrucksMap.get(`${row.truck.id}_${row.truck.currentLocationId ?? ''}`) ?? null,
    [repairTrucksMap]
  )
  const transportTrucksById = useMemo(
    () => new Map(transportTrucksWithVehicles.map((t) => [t.truck.id, t] as const)),
    [transportTrucksWithVehicles]
  )

  const repairTrackingItems = useMemo((): RepairTruckWithTruck[] => {
    const list: RepairTruckWithTruck[] = []
    allRepairTrucks.forEach((repairTruck) => {
      const truckWithVehicle = transportTrucksById.get(repairTruck.transport_truck_id)
      if (!truckWithVehicle) return
      list.push({ repairTruck, truckWithVehicle })
    })
    return list.sort(
      (a, b) =>
        (b.repairTruck.created_at?.toMillis?.() ?? 0) -
        (a.repairTruck.created_at?.toMillis?.() ?? 0)
    )
  }, [allRepairTrucks, transportTrucksById])

  const isCurrentRepairItem = useCallback((item: RepairTruckWithTruck) => {
    const status = getTruckStatus(item.truckWithVehicle.truck)
    return isTruckActiveAtRepairShop({
      truckStatus: status,
      currentLocationId: item.truckWithVehicle.truck.currentLocationId,
      repairShopLocationId: item.repairTruck.repair_shop_location_id,
    })
  }, [])

  const repairTrackingItemsByMode = useMemo((): RepairTruckWithTruck[] => {
    const latestRepairByTruckId = new Map<string, RepairTruckWithTruck>()
    repairTrackingItems.forEach((item) => {
      const truckId = item.truckWithVehicle.truck.id
      const existing = latestRepairByTruckId.get(truckId)
      if (
        !existing ||
        (item.repairTruck.created_at?.toMillis?.() ?? 0) >=
          (existing.repairTruck.created_at?.toMillis?.() ?? 0)
      ) {
        latestRepairByTruckId.set(truckId, item)
      }
    })

    const byMode = repairTrackingItems.filter((item) => {
      const isCurrent = isCurrentRepairItem(item)
      const latestForTruck = latestRepairByTruckId.get(item.truckWithVehicle.truck.id)
      const isLatestForTruck = latestForTruck?.repairTruck.id === item.repairTruck.id
      if (showPastRepairs) return !isCurrent || !isLatestForTruck
      return isCurrent && isLatestForTruck
    })
    if (!flaggedOnly) return byMode
    return byMode.filter((item) => Boolean(item.repairTruck.flagged))
  }, [repairTrackingItems, showPastRepairs, isCurrentRepairItem, flaggedOnly])

  const customerShopItems = repairTrackingItemsByMode

  const applyHeaderFilters = useCallback(
    (items: RepairTruckWithTruck[]): RepairTruckWithTruck[] => {
      return items.filter((item) => {
        if (statusFilter !== ALL_FILTER && getInventoryStatusFilterValue(item) !== statusFilter) {
          return false
        }
        if (
          workStatusFilter !== ALL_FILTER &&
          getRepairTruckWorkStatus(item.repairTruck) !== workStatusFilter
        ) {
          return false
        }
        return true
      })
    },
    [statusFilter, workStatusFilter]
  )

  const statusFilterOptions = useMemo(() => {
    const counts = new Map<string, number>()
    repairTrackingItemsByMode.forEach((item) => {
      const value = getInventoryStatusFilterValue(item)
      counts.set(value, (counts.get(value) ?? 0) + 1)
    })
    const knownStatuses = vehicleStatusOptions.filter((status) => counts.has(status))
    const extras = [...counts.keys()]
      .filter((value) => value !== UNKNOWN_STATUS_FILTER && !vehicleStatusOptions.includes(value))
      .sort((a, b) => a.localeCompare(b))
    return [
      { value: ALL_FILTER, label: 'All statuses' },
      ...knownStatuses.map((status) => ({
        value: status,
        label: status,
        count: counts.get(status) ?? 0,
      })),
      ...extras.map((status) => ({
        value: status,
        label: status,
        count: counts.get(status) ?? 0,
      })),
      ...(counts.has(UNKNOWN_STATUS_FILTER)
        ? [{ value: UNKNOWN_STATUS_FILTER, label: 'Unknown', count: counts.get(UNKNOWN_STATUS_FILTER) ?? 0 }]
        : []),
    ]
  }, [repairTrackingItemsByMode])

  const workStatusFilterOptions = useMemo(() => {
    const counts = new Map<string, number>()
    repairTrackingItemsByMode.forEach((item) => {
      const value = getRepairTruckWorkStatus(item.repairTruck)
      counts.set(value, (counts.get(value) ?? 0) + 1)
    })
    return [
      { value: ALL_FILTER, label: 'All work statuses' },
      ...WORK_STATUS_FILTER_OPTIONS.map((option) => ({
        ...option,
        count: counts.get(option.value) ?? 0,
      })),
    ]
  }, [repairTrackingItemsByMode])

  const shopTabs = useMemo(() => {
    const ids = [
      ...new Set(customerShopItems.map((item) => item.repairTruck.repair_shop_location_id).filter(Boolean)),
    ] as string[]
    const hasUnknown = customerShopItems.some((item) => !item.repairTruck.repair_shop_location_id)
    const sorted = ids.slice().sort((a, b) => {
      const labelA = getShopLabel(a, repairShopLocations)
      const labelB = getShopLabel(b, repairShopLocations)
      if (labelA === UNKNOWN_SHOP_LABEL) return 1
      if (labelB === UNKNOWN_SHOP_LABEL) return -1
      return labelA.localeCompare(labelB)
    })
    return [
      { id: ALL_SHOPS, label: 'All' },
      ...sorted.map((id) => ({ id, label: getShopLabel(id, repairShopLocations) })),
      ...(hasUnknown ? [{ id: UNKNOWN_SHOP_ID as string, label: UNKNOWN_SHOP_LABEL }] : []),
    ]
  }, [customerShopItems, repairShopLocations])

  const filteredShopItems = useMemo((): RepairTruckWithTruck[] => {
    if (selectedShopId === ALL_SHOPS) return customerShopItems
    if (selectedShopId === UNKNOWN_SHOP_ID)
      return customerShopItems.filter((item) => !item.repairTruck.repair_shop_location_id)
    return customerShopItems.filter(
      (item) => item.repairTruck.repair_shop_location_id === selectedShopId
    )
  }, [customerShopItems, selectedShopId])

  const getShopLabelMemo = useCallback(
    (locationId: string | undefined) => getShopLabel(locationId, repairShopLocations),
    [repairShopLocations]
  )

  const customerTabs = useMemo(() => {
    const ids = [
      ...new Set(customerShopItems.map((item) => getClientUserIdFromTruck(item.truckWithVehicle)).filter(Boolean)),
    ] as string[]
    const hasUnassigned = customerShopItems.some(
      (item) => !getClientUserIdFromTruck(item.truckWithVehicle)
    )
    const sorted = ids.slice().sort((a, b) => {
      const labelA = getCustomerLabel(a, clientUsers)
      const labelB = getCustomerLabel(b, clientUsers)
      return labelA.localeCompare(labelB)
    })
    return [
      { id: ALL_CUSTOMERS, label: 'All' },
      ...sorted.map((id) => ({ id, label: getCustomerLabel(id, clientUsers) })),
      ...(hasUnassigned ? [{ id: UNASSIGNED_CUSTOMER_ID as string, label: UNASSIGNED_CUSTOMER_LABEL }] : []),
    ]
  }, [customerShopItems, clientUsers])

  const filteredItemsByCustomer = useMemo((): RepairTruckWithTruck[] => {
    if (selectedCustomerId === ALL_CUSTOMERS) return customerShopItems
    if (selectedCustomerId === UNASSIGNED_CUSTOMER_ID)
      return customerShopItems.filter((item) => !getClientUserIdFromTruck(item.truckWithVehicle))
    return customerShopItems.filter(
      (item) => getClientUserIdFromTruck(item.truckWithVehicle) === selectedCustomerId
    )
  }, [customerShopItems, selectedCustomerId])

  const getCustomerLabelMemo = useCallback(
    (row: TransportTruckWithVehicle) => {
      const vehicle = row.vehicle
      const clientUserId = getClientUserIdFromTruck(row)
      if (clientUserId && clientUserId !== 'optic') {
        const user = clientUsers?.find((u) => u.id === clientUserId)
        const label = user?.displayName ?? user?.email ?? clientUserId
        return label
      }

      if (vehicle?.sold_to_user?.name?.trim()) return UNASSIGNED_CUSTOMER_LABEL
      if (
        vehicle?.selling_to_user?.name?.trim() &&
        PENDING_CUSTOMER_STATUSES.has(vehicle.status ?? '')
      ) {
        return UNASSIGNED_CUSTOMER_LABEL
      }
      if (vehicle?.selling_to_user?.name?.trim()) return UNASSIGNED_CUSTOMER_LABEL
      return UNASSIGNED_CUSTOMER_LABEL
    },
    [clientUsers]
  )

  const getCustomerDisplay = useCallback(
    (row: RepairTruckWithTruck): CustomerDisplay => ({
      label: getCustomerLabelMemo(row.truckWithVehicle),
    }),
    [getCustomerLabelMemo]
  )

  const getInventoryStatusDisplay = useCallback(
    (row: RepairTruckWithTruck): InventoryStatusDisplay => {
      const status = row.truckWithVehicle.vehicle?.status ?? null
      const soldToName = row.truckWithVehicle.vehicle?.sold_to_user?.name?.trim()
      const sellingToName = row.truckWithVehicle.vehicle?.selling_to_user?.name?.trim()
      if (!status) return { label: 'Unknown', chipColor: 'default' }
      if (status === 'Sold') {
        return { label: soldToName ? `Sold: ${soldToName}` : status, chipColor: 'error' }
      }
      if (status === 'Pending Sale') {
        return { label: sellingToName ? `Pending Sale: ${sellingToName}` : status, chipColor: 'warning' }
      }
      if (status === 'Active Deal') {
        return { label: sellingToName ? `Active Deal: ${sellingToName}` : status, chipColor: 'info' }
      }
      return { label: status, chipColor: 'default' }
    },
    []
  )

  const pickupLocationsById = useMemo(
    () => new Map((pickupLocations ?? []).map((location) => [location.id, location])),
    [pickupLocations]
  )

  const selectedCustomerContact = useMemo(() => {
    if (selectedCustomerId === ALL_CUSTOMERS) return null

    const rowsForSelection =
      selectedCustomerId === UNASSIGNED_CUSTOMER_ID
        ? customerShopItems.filter((item) => !getClientUserIdFromTruck(item.truckWithVehicle))
        : customerShopItems.filter(
            (item) => getClientUserIdFromTruck(item.truckWithVehicle) === selectedCustomerId
          )

    if (rowsForSelection.length === 0) return null

    const latestPlan = rowsForSelection
      .map((item) => getLatestShipmentPlanFromTruck(item.truckWithVehicle))
      .filter((plan): plan is NonNullable<ReturnType<typeof getLatestShipmentPlanFromTruck>> => !!plan)
      .sort((a, b) => (b.created_at?.toMillis?.() ?? 0) - (a.created_at?.toMillis?.() ?? 0))[0]

    const finalLocation = latestPlan?.locations?.[latestPlan.locations.length - 1]
    if (!finalLocation || finalLocation.location_type !== 'pickup') return null

    const pickupLocation = pickupLocationsById.get(finalLocation.location_id)
    if (!pickupLocation) return null

    return {
      name: pickupLocation.contact_name || 'Unknown contact',
      phone: pickupLocation.contact_phone || 'No phone',
      address: formatLocationAddress(pickupLocation),
    }
  }, [selectedCustomerId, customerShopItems, pickupLocationsById])

  const selectedShopContact = useMemo(() => {
    if (selectedShopId === ALL_SHOPS) return null
    if (selectedShopId === UNKNOWN_SHOP_ID) {
      return {
        name: UNKNOWN_SHOP_LABEL,
        phone: 'No phone',
        address: '—',
      }
    }
    const shop = repairShopLocations?.find((s) => s.id === selectedShopId)
    if (!shop) return null
    return {
      name: shop.name || shop.contact_name || 'Unknown shop',
      phone: shop.contact_phone || 'No phone',
      address: formatLocationAddress(shop),
    }
  }, [selectedShopId, repairShopLocations])

  const repairTrackingItemsFiltered = useMemo((): RepairTruckWithTruck[] => {
    const term = searchTerm.trim()
    const baseRows = applyHeaderFilters(repairTrackingItemsByMode)
    const filtered = !term
      ? baseRows
      : baseRows.filter((item) =>
          truckMatchesSearch(item.truckWithVehicle, getCustomerLabelMemo, searchTerm)
        )
    return sortRepairItems(filtered, sortBy)
  }, [repairTrackingItemsByMode, searchTerm, getCustomerLabelMemo, sortBy, applyHeaderFilters])

  const filteredCustomerItemsSearch = useMemo((): RepairTruckWithTruck[] => {
    const baseRows = applyHeaderFilters(filteredItemsByCustomer)
    const term = searchTerm.trim()
    const filtered = !term
      ? baseRows
      : baseRows.filter((row) =>
          truckMatchesSearch(row.truckWithVehicle, getCustomerLabelMemo, searchTerm)
        )
    return sortRepairItems(filtered, sortBy)
  }, [filteredItemsByCustomer, searchTerm, getCustomerLabelMemo, sortBy, applyHeaderFilters])

  const filteredShopItemsSearch = useMemo((): RepairTruckWithTruck[] => {
    const baseRows = applyHeaderFilters(filteredShopItems)
    const term = searchTerm.trim()
    const filtered = !term
      ? baseRows
      : baseRows.filter((row) =>
          truckMatchesSearch(row.truckWithVehicle, getCustomerLabelMemo, searchTerm)
        )
    return sortRepairItems(filtered, sortBy, { usePriorityDefault: true })
  }, [filteredShopItems, searchTerm, getCustomerLabelMemo, sortBy, applyHeaderFilters])

  const isShopPriorityReorderEnabled =
    selectedShopId !== ALL_SHOPS &&
    selectedShopId !== UNKNOWN_SHOP_ID &&
    !showPastRepairs &&
    !flaggedOnly &&
    !searchTerm.trim() &&
    statusFilter === ALL_FILTER &&
    workStatusFilter === ALL_FILTER &&
    sortBy === 'default' &&
    !isUpdatingRepairTruckPriorities

  const repairSearchHeaderFilters = {
    showStatusFilter: true,
    statusFilter,
    onStatusFilterChange: setStatusFilter,
    statusFilterOptions,
    statusFilterLabel: 'Filter by Status',
    showWorkStatusFilter: true,
    workStatusFilter,
    onWorkStatusFilterChange: setWorkStatusFilter,
    workStatusFilterOptions,
  } as const

  const handleReorderShopPriorities = useCallback(
    async (orderedRows: RepairTruckWithTruck[]) => {
      if (!isShopPriorityReorderEnabled) return
      if (selectedShopId === ALL_SHOPS || selectedShopId === UNKNOWN_SHOP_ID) return

      const orderedRepairTruckIds = orderedRows
        .map((row) => row.repairTruck.id)
        .filter((id): id is string => Boolean(id))

      if (orderedRepairTruckIds.length === 0) return

      await updateRepairTruckPriorities({
        repairShopLocationId: selectedShopId,
        orderedRepairTruckIds,
      })
    },
    [
      isShopPriorityReorderEnabled,
      selectedShopId,
      updateRepairTruckPriorities,
    ]
  )

  const selectedTimelineItem = useMemo<RepairTruckWithTruck | null>(() => {
    if (!selectedTimelineRepairTruckId || !selectedTimelineTruckId) return null
    const repairTruck = allRepairTrucks.find((r) => r.id === selectedTimelineRepairTruckId)
    const truckWithVehicle = transportTrucksById.get(selectedTimelineTruckId)
    if (!repairTruck || !truckWithVehicle) return null
    return { repairTruck, truckWithVehicle }
  }, [selectedTimelineRepairTruckId, selectedTimelineTruckId, allRepairTrucks, transportTrucksById])

  const handleOpenTimeline = useCallback(
    (row: RepairTruckWithTruck) => {
      setSelectedTimelineRepairTruckId(row.repairTruck.id)
      setSelectedTimelineTruckId(row.truckWithVehicle.truck.id)
    },
    []
  )

  const handleViewTransportCard = useCallback((item: RepairTruckWithTruck) => {
    setTransportCardTruckId(item.truckWithVehicle.truck.id)
  }, [])

  const handleOpenInventoryForm = useCallback((inventoryVehicleId: string) => {
    setInventoryFormVehicleId(inventoryVehicleId)
  }, [])

  if (isLoadingTransportTrucks) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ px: 2 }}>
        <Tabs
          value={innerTab}
          onChange={(_, v) => setInnerTab(v)}
          textColor="primary"
          sx={{
            mt: 1,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            '& .MuiTabs-flexContainer': { gap: 0.5 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              minHeight: 40,
              color: 'text.primary',
              px: 2,
              borderRadius: 1.5,
              '&.Mui-selected': {
                color: 'primary.contrastText',
                backgroundColor: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.main',
              },
            },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Repair tracking" />
          <Tab label="Customers" />
          <Tab label="Shops" />
        </Tabs>
      </Box>

      {innerTab === 0 && (
        <Box sx={{ p: 2 }}>
          <SearchHeader
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search by VIN, make, model, year, stock #, client..."
            showSearch={true}
            showActiveCompletedToggle={true}
            isActiveFilter={!showPastRepairs}
            onActiveCompletedToggle={(isActive) => setShowPastRepairs(!isActive)}
            showFlaggedCheckbox={true}
            flaggedOnly={flaggedOnly}
            onFlaggedOnlyChange={setFlaggedOnly}
            showSortBy={true}
            sortBy={sortBy}
            onSortByChange={(value) => setSortBy(value as RepairSortOption)}
            sortByOptions={REPAIR_SORT_OPTIONS}
            {...repairSearchHeaderFilters}
          />
          {repairTrackingItemsFiltered.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {repairTrackingItemsByMode.length === 0
                ? showPastRepairs
                  ? 'No past repairs found.'
                  : 'No active repair tracking records. Trucks currently in repair will appear here as cards.'
                : 'No matches for your search.'}
            </Typography>
          ) : (
            <RepairTrackingView
              items={repairTrackingItemsFiltered}
              getRepairShopLabel={(item) => getShopLabelMemo(item.repairTruck.repair_shop_location_id)}
              getUserName={getUserName}
              onAddNote={async (id, payload) => {
                await addRepairTruckNote({
                  repairTruckId: id,
                  note: payload.note,
                  visible_to_repair_users: payload.visible_to_repair_users,
                })
              }}
              onEditNote={async (id, noteIndex, note) => {
                await updateRepairTruckNote({ repairTruckId: id, noteIndex, note })
              }}
              onSetNoteVisibleToRepairUsers={async (id, noteIndex, visible) => {
                await updateRepairTruckNote({
                  repairTruckId: id,
                  noteIndex,
                  visible_to_repair_users: visible,
                })
              }}
              onDeleteNote={async (id, noteIndex) => {
                await deleteRepairTruckNote({ repairTruckId: id, noteIndex })
              }}
              onUpdateWorkStatus={async (id, work_status) => {
                await updateRepairTruckWorkStatus({ repairTruckId: id, work_status })
              }}
              onReviewEstimate={async (id, noteIndex, status) => {
                await reviewRepairTruckEstimate({ repairTruckId: id, noteIndex, status })
              }}
              onUpdatePaid={async (id, paid) => {
                await updateRepairTruckPaid({ repairTruckId: id, paid })
              }}
              onUpdateBeingPainted={async (id, being_painted) => {
                await updateRepairTruckBeingPainted({ repairTruckId: id, being_painted })
              }}
              onToggleFlag={async (id, flagged) => {
                await updateRepairTruckFlagged({ repairTruckId: id, flagged })
              }}
              isAddingNote={isAddingRepairTruckNote}
              isEditingNote={isUpdatingRepairTruckNote}
              isDeletingNote={isDeletingRepairTruckNote}
              isUpdatingWorkStatus={isUpdatingRepairTruckWorkStatus}
              isReviewingEstimate={isReviewingRepairTruckEstimate}
              isUpdatingPaid={isUpdatingRepairTruckPaid}
              isUpdatingBeingPainted={isUpdatingRepairTruckBeingPainted}
              isUpdatingFlag={isUpdatingRepairTruckFlagged}
            />
          )}
        </Box>
      )}

      {innerTab === 1 && (
        <Box sx={{ p: 2 }}>
          <SearchHeader
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search by VIN, make, model, year, stock #, client..."
            showSearch={true}
            showActiveCompletedToggle={true}
            isActiveFilter={!showPastRepairs}
            onActiveCompletedToggle={(isActive) => setShowPastRepairs(!isActive)}
            showFlaggedCheckbox={true}
            flaggedOnly={flaggedOnly}
            onFlaggedOnlyChange={setFlaggedOnly}
            showSortBy={true}
            sortBy={sortBy}
            onSortByChange={(value) => setSortBy(value as RepairSortOption)}
            sortByOptions={REPAIR_SORT_OPTIONS}
            {...repairSearchHeaderFilters}
          />
          {customerShopItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {showPastRepairs
                ? 'No completed repairs found.'
                : 'No trucks currently in repair. Trucks with status "Repair" will appear here, grouped by customer.'}
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.100',
                  mb: 2,
                  minHeight: 40,
                }}
              >
                {customerTabs.map(({ id, label }) => {
                  const isSelected = selectedCustomerId === id
                  return (
                    <Button
                      key={id}
                      onClick={() => setSelectedCustomerId(id)}
                      sx={{
                        minWidth: 100,
                        maxWidth: 220,
                        px: 1.5,
                        textTransform: 'none',
                        borderRadius: 0,
                        borderBottom: isSelected ? 2 : 0,
                        borderColor: 'primary.main',
                        bgcolor: isSelected ? 'background.paper' : 'transparent',
                        color: isSelected ? 'primary.main' : 'text.primary',
                        '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {label}
                      </Typography>
                    </Button>
                  )
                })}
              </Box>
              {selectedCustomerId !== ALL_CUSTOMERS && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 2,
                    borderColor: 'divider',
                    backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.primary"
                    fontWeight={600}
                    sx={{ textTransform: 'uppercase' }}
                  >
                    Customer contact
                  </Typography>
                  {selectedCustomerContact ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedCustomerContact.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCustomerContact.phone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCustomerContact.address}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      No final pickup location contact found.
                    </Typography>
                  )}
                </Paper>
              )}
              {filteredCustomerItemsSearch.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No matches for your search.
                </Typography>
              ) : (
                <RepairV2TrucksGrid
                  rowData={filteredCustomerItemsSearch}
                  getRepairShopLabel={(row) =>
                    getShopLabelMemo(row.repairTruck.repair_shop_location_id)
                  }
                  getCustomerLabel={(row) => getCustomerDisplay(row).label}
                  getInventoryStatusDisplay={getInventoryStatusDisplay}
                  onOpenTimeline={handleOpenTimeline}
                  onOpenTransportTruck={handleViewTransportCard}
                  onOpenInventoryForm={handleOpenInventoryForm}
                  onToggleFlag={(repairTruck, flagged) =>
                    updateRepairTruckFlagged({ repairTruckId: repairTruck.id, flagged })
                  }
                  isTogglingFlag={isUpdatingRepairTruckFlagged}
                />
              )}
            </>
          )}
        </Box>
      )}

      {innerTab === 2 && (
        <Box sx={{ p: 2 }}>
          <SearchHeader
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search by VIN, make, model, year, stock #, client..."
            showSearch={true}
            showActiveCompletedToggle={true}
            isActiveFilter={!showPastRepairs}
            onActiveCompletedToggle={(isActive) => setShowPastRepairs(!isActive)}
            showFlaggedCheckbox={true}
            flaggedOnly={flaggedOnly}
            onFlaggedOnlyChange={setFlaggedOnly}
            showSortBy={true}
            sortBy={sortBy}
            onSortByChange={(value) => setSortBy(value as RepairSortOption)}
            sortByOptions={REPAIR_SORT_OPTIONS}
            {...repairSearchHeaderFilters}
          />
          {customerShopItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {showPastRepairs
                ? 'No completed repairs found.'
                : 'No trucks currently in repair. Trucks with status "Repair" will appear here, grouped by shop.'}
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.100',
                  mb: 2,
                  minHeight: 40,
                }}
              >
                {shopTabs.map(({ id, label }) => {
                  const isSelected = selectedShopId === id
                  return (
                    <Button
                      key={id}
                      onClick={() => setSelectedShopId(id)}
                      sx={{
                        minWidth: 100,
                        maxWidth: 220,
                        px: 1.5,
                        textTransform: 'none',
                        borderRadius: 0,
                        borderBottom: isSelected ? 2 : 0,
                        borderColor: 'primary.main',
                        bgcolor: isSelected ? 'background.paper' : 'transparent',
                        color: isSelected ? 'primary.main' : 'text.primary',
                        '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {label}
                      </Typography>
                    </Button>
                  )
                })}
              </Box>
              {selectedShopId !== ALL_SHOPS && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 2,
                    borderColor: 'divider',
                    backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.primary"
                    fontWeight={600}
                    sx={{ textTransform: 'uppercase' }}
                  >
                    Repair shop contact
                  </Typography>
                  {selectedShopContact ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedShopContact.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedShopContact.phone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedShopContact.address}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Average repair time:{' '}
                        {selectedShopId === UNKNOWN_SHOP_ID
                          ? '—'
                          : formatAverageDuration(repairShopAvgDropoffToCompleteMsById[selectedShopId])}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      No repair shop contact found.
                    </Typography>
                  )}
                </Paper>
              )}
              {filteredShopItemsSearch.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No matches for your search.
                </Typography>
              ) : (
                <RepairV2TrucksGrid
                  rowData={filteredShopItemsSearch}
                  getRepairShopLabel={(row) =>
                    getShopLabelMemo(row.repairTruck.repair_shop_location_id)
                  }
                  getCustomerLabel={(row) => getCustomerDisplay(row).label}
                  getInventoryStatusDisplay={getInventoryStatusDisplay}
                  getPriorityRank={(row) =>
                    typeof row.repairTruck.priority_rank === 'number'
                      ? row.repairTruck.priority_rank
                      : null
                  }
                  enablePriorityReorder={isShopPriorityReorderEnabled}
                  onReorderPriority={handleReorderShopPriorities}
                  onOpenTimeline={handleOpenTimeline}
                  onOpenTransportTruck={handleViewTransportCard}
                  onOpenInventoryForm={handleOpenInventoryForm}
                  onToggleFlag={(repairTruck, flagged) =>
                    updateRepairTruckFlagged({ repairTruckId: repairTruck.id, flagged })
                  }
                  isTogglingFlag={isUpdatingRepairTruckFlagged}
                />
              )}
            </>
          )}
        </Box>
      )}
      <RepairTimelineDialog
        open={!!selectedTimelineRepairTruckId}
        onClose={() => {
          setSelectedTimelineRepairTruckId(null)
          setSelectedTimelineTruckId(null)
        }}
        item={selectedTimelineItem}
        getRepairShopLabel={(item) => getShopLabelMemo(item.repairTruck.repair_shop_location_id)}
        getUserName={getUserName}
        onAddNote={async (id, payload) => {
          await addRepairTruckNote({
            repairTruckId: id,
            note: payload.note,
            visible_to_repair_users: payload.visible_to_repair_users,
          })
        }}
        onEditNote={async (id, noteIndex, note) => {
          await updateRepairTruckNote({ repairTruckId: id, noteIndex, note })
        }}
        onSetNoteVisibleToRepairUsers={async (id, noteIndex, visible) => {
          await updateRepairTruckNote({
            repairTruckId: id,
            noteIndex,
            visible_to_repair_users: visible,
          })
        }}
        onDeleteNote={async (id, noteIndex) => {
          await deleteRepairTruckNote({ repairTruckId: id, noteIndex })
        }}
        onUpdateWorkStatus={async (id, work_status) => {
          await updateRepairTruckWorkStatus({ repairTruckId: id, work_status })
        }}
        onReviewEstimate={async (id, noteIndex, status) => {
          await reviewRepairTruckEstimate({ repairTruckId: id, noteIndex, status })
        }}
        onUpdatePaid={async (id, paid) => {
          await updateRepairTruckPaid({ repairTruckId: id, paid })
        }}
        onUpdateBeingPainted={async (id, being_painted) => {
          await updateRepairTruckBeingPainted({ repairTruckId: id, being_painted })
        }}
        onToggleFlag={async (id, flagged) => {
          await updateRepairTruckFlagged({ repairTruckId: id, flagged })
        }}
        isAddingNote={isAddingRepairTruckNote}
        isEditingNote={isUpdatingRepairTruckNote}
        isDeletingNote={isDeletingRepairTruckNote}
        isUpdatingWorkStatus={isUpdatingRepairTruckWorkStatus}
        isReviewingEstimate={isReviewingRepairTruckEstimate}
        isUpdatingPaid={isUpdatingRepairTruckPaid}
        isUpdatingBeingPainted={isUpdatingRepairTruckBeingPainted}
        isUpdatingFlag={isUpdatingRepairTruckFlagged}
      />
      <EditVehicleForm
        open={!!inventoryFormVehicleId}
        vehicleId={inventoryFormVehicleId ?? undefined}
        onClose={() => setInventoryFormVehicleId(null)}
        inventoryCollection="master_inventory"
      />
      <TransportTruckCardDialog
        open={!!transportCardTruckId}
        onClose={() => setTransportCardTruckId(null)}
        truckId={transportCardTruckId}
      />
    </Box>
  )
}

export default RepairV2Tab
